import tw from "twin.macro";

import React, { useCallback, useEffect } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";

import { Button } from "@mui/material";

import { addDays } from "date-fns";

import { RecessCard } from "@components/StyledComponents";
import { BlockCard, HelperTextLabel, SaveFormBanner } from "@features/ui";
import { OrderWindow } from "@models/OrderWindow";
import {
  ControlledDateInput,
  ControlledResourceAutocomplete,
  ControlledTextInput,
  usePersistFormChanges,
} from "@utils/forms";

import {
  useCreateOrderWindowMutation,
  useUpdateOrderWindowMutation,
} from "../data";
import OrderWindowProgramsTile from "./OrderWindowProgramsTile";

const TwoCol = tw.div`grid gap-x-6 gap-y-4 md:grid-cols-2`;

const dateFieldsInOrder = {
  openDate: "Open Date",
  closeDate: "Close Date",
  reviewCloseDate: "Review Close Date",
  inMarketDate: "In-Market Date",
  demandPeriodEndDate: (
    <HelperTextLabel title="Defines how long inventory from this Order Window is expected to cover demand. Used to calculate recommended order quantities in Demand Planning.">
      Demand Coverage End Date
    </HelperTextLabel>
  ),
};

const formValuesFromOrderWindow = (ow: OrderWindow | null) => {
  return {
    name: ow?.name ?? "",
    openDate: ow?.openDate ?? "",
    closeDate: ow?.closeDate ?? "",
    reviewCloseDate: ow?.reviewCloseDate ?? "",
    inMarketDate: ow?.inMarketDate ?? "",
    demandPeriodEndDate: ow?.demandPeriodEndDate ?? "",
    programIds: ow?.programs?.map((p) => p.id) ?? [],
    channelId: ow?.channel?.id ?? null,
  };
};

type FormValues = ReturnType<typeof formValuesFromOrderWindow>;

interface OrderWindowFormTilesProps {
  orderWindow: OrderWindow | null;
}

const OrderWindowFormTiles: React.FC<OrderWindowFormTilesProps> = ({
  orderWindow,
}) => {
  const navigate = useNavigate();
  const createMutation = useCreateOrderWindowMutation();
  const updateMutation = useUpdateOrderWindowMutation();

  const { usesChannels, usesDemandPlanning } = useSelector(
    (state: any) => state.currentUser.organization
  );

  const formMethods = useForm<FormValues>({
    defaultValues: formValuesFromOrderWindow(orderWindow),
  });
  const {
    control,
    handleSubmit,
    formState: { isDirty },
    reset,
    watch,
  } = formMethods;

  const { clearStorage } = usePersistFormChanges(
    `order-window-${orderWindow?.id ?? "new"}`,
    formMethods
  );

  const channelId = watch("channelId");
  const dateValues = watch(
    Object.keys(dateFieldsInOrder) as (keyof FormValues)[]
  ) as string[];

  const reg = (name: string, rules: any) => ({
    name,
    control,
    rules,
  });

  const regOrderWindow = (name: string) => {
    const dateFieldnames = Object.keys(dateFieldsInOrder);
    const dateIndex = dateFieldnames.indexOf(name);
    let rules = {};

    // Each date must be at least 1 day after the previous date.
    const minDate =
      dateIndex < 1
        ? undefined
        : addDays(new Date(dateValues[dateIndex - 1]!), 1);

    if (name !== "demandPeriodEndDate") {
      rules = { required: `${dateFieldsInOrder[name]} is required` };
    }
    return {
      name,
      control,
      rules,
      minDate,
    };
  };

  const onSubmit: SubmitHandler<FormValues> = async (formData) => {
    if (orderWindow) {
      updateMutation.mutate({ id: orderWindow.id, payload: formData });
    } else {
      createMutation.mutate(formData, {
        onSuccess: (ow) => {
          clearStorage();
          navigate(`/admin/order-windows/${ow.id}`, { replace: true });
        },
      });
    }
  };

  const handleReset = useCallback(() => {
    reset(formValuesFromOrderWindow(orderWindow));
  }, [reset, orderWindow]);

  useEffect(() => {
    handleReset();
  }, [handleReset]);

  return (
    <FormProvider {...formMethods}>
      {orderWindow && isDirty && (
        <SaveFormBanner
          isLoading={createMutation.isPending || updateMutation.isPending}
          handleSave={handleSubmit(onSubmit)}
          handleReset={handleReset}
        />
      )}
      <form onSubmit={handleSubmit(onSubmit)} tw="space-y-6">
        <BlockCard title="General Information">
          <TwoCol tw="mb-6">
            <ControlledTextInput
              {...reg("name", { required: "Name is required" })}
              label="Order Window Name"
            />
          </TwoCol>
          <TwoCol>
            {Object.entries(dateFieldsInOrder)
              .filter(
                ([f, _l]) => f !== "demandPeriodEndDate" || usesDemandPlanning
              )
              .map(([name, label]) => {
                return (
                  <ControlledDateInput
                    key={name}
                    css={
                      //Force inMarketDate and DemandPeriodEndDate to be on the same row
                      usesDemandPlanning &&
                      name === "inMarketDate" &&
                      tw`col-start-1`
                    }
                    {...regOrderWindow(name)}
                    label={label}
                  />
                );
              })}
          </TwoCol>
        </BlockCard>

        {usesChannels && (
          <BlockCard title="Visibility">
            <TwoCol>
              <ControlledResourceAutocomplete
                resource="channels"
                {...reg("channelId", { required: "Channel is required" })}
                label="Channel"
                tw="max-w-prose"
              />
            </TwoCol>
          </BlockCard>
        )}

        {usesChannels && !channelId ? (
          <RecessCard>
            <h2 tw="text-lg">Select a Channel to assign programs.</h2>
          </RecessCard>
        ) : (
          <OrderWindowProgramsTile />
        )}
        {!orderWindow && (
          <Button variant="contained" color="primary" type="submit">
            Create Order Window
          </Button>
        )}
      </form>
    </FormProvider>
  );
};

export default OrderWindowFormTiles;
